import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { MessageSquare, Filter, X } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../../lib/api";
import { FILTER_LABELS } from "../../lib/feedbackLink";
import { Label, EmptyState, InfoBadge } from "../../components/common";
import { Button } from "../../components/ui/button";
import FeedbackCard from "../../components/FeedbackCard";
import FeedbackDetailSheet from "../../components/FeedbackDetailSheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";

const STATUSES = ["TRUSTED", "LOW_RISK", "REVIEW", "HIGH_RISK", "REJECTED"];
const KEYS = Object.keys(FILTER_LABELS).filter((k) => k !== "review_status");

function chipText(k, v, rows) {
  if (k === "location_id") return rows[0]?.location_name || "Selected location";
  if (k === "employee_id") return rows[0]?.employee_name || "Selected employee";
  if (k === "point_id") return rows[0]?.point_name || "Selected counter/table";
  if (k === "rating") return `${v}★`;
  if (k === "has_comment") return "With comment";
  if (k === "low_dim") return "< 3";
  if (k === "answer" || k === "resolved") return v === "yes" ? "Yes" : "No";
  if (k === "days") return String(v) === "0" ? "Today" : `Last ${v} days`;
  if (k === "dimension") {
    // Resolve custom-dim key (e.g. cdim_xxxx) to its label via the snapshot carried on
    // each feedback. Fallback keeps the chip readable for removed dims.
    for (const r of rows) {
      const lbl = r?.dim_labels?.[v];
      if (lbl) return lbl;
    }
    return String(v).startsWith("cdim_") ? "Removed criterion" : String(v).replace(/_/g, " ");
  }
  return String(v).replace(/_/g, " ");
}

export default function FeedbackPage() {
  const [rows, setRows] = useState([]);
  const [params, setParams] = useSearchParams();
  useEffect(() => {
    if (!params.get("category")) return;
    const p = new URLSearchParams(params); p.set("problem_category", p.get("category")); p.delete("category"); setParams(p, { replace: true });
  }, [params, setParams]);
  const filter = params.get("review_status") || "all";
  const openId = params.get("open");
  const lowDimOn = params.get("low_dim") === "true";
  const active = KEYS.filter((k) => params.get(k));
  const qs = [...active, ...(filter !== "all" ? ["review_status"] : [])].map((k) => `${k}=${encodeURIComponent(params.get(k))}`).join("&");

  const load = useCallback(() => {
    api.get(`/responses/feedback${qs ? `?${qs}` : ""}`).then((r) => setRows(r.data)).catch((e) => toast.error(apiError(e)));
  }, [qs]);
  useEffect(() => { load(); }, [load]);

  const setParam = (k, v) => { const p = new URLSearchParams(params); v ? p.set(k, v) : p.delete(k); setParams(p); };
  const drop = (k) => {
    const p = new URLSearchParams(params); p.delete(k);
    if (k === "question" || k === "touchpoint") p.delete("answer");
    if (k === "dimension") p.delete("dim_value");
    setParams(p);
  };

  const moderate = async (fb, status) => {
    try {
      await api.patch(`/feedback/${fb.id}/moderate`, { review_status: status, reason: `Manual review → ${status}` });
      toast.success(`Marked ${status.replace("_", " ")}`); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div><span className="flex items-center gap-1.5"><Label>Customer Voice</Label>
          <InfoBadge title="Feedback & Moderation" testId="feedback-header-info"
            text="Every customer rating and comment, with a trust status from our anti-gaming engine. Click any rating to see everything the customer submitted. Filters from dashboards and reports appear as chips you can remove." /></span>
          <h1 className="font-head text-2xl font-bold text-slate-900 sm:text-3xl">Feedback & Moderation</h1></div>
        <div className="flex items-center gap-2">
          <Button
            variant={lowDimOn ? "default" : "outline"}
            size="sm"
            onClick={() => setParam("low_dim", lowDimOn ? "" : "true")}
            data-testid="feedback-low-dim-toggle"
            className={lowDimOn ? "bg-red-600 hover:bg-red-700 text-white" : "text-red-700 border-red-200 hover:bg-red-50"}
          >
            Any dim &lt; 3
          </Button>
          <Filter className="h-4 w-4 text-slate-400" />
          <Select value={filter} onValueChange={(v) => setParam("review_status", v === "all" ? "" : v)}>
            <SelectTrigger className="w-44" data-testid="feedback-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All feedback</SelectItem>
              {STATUSES.map((s) => <SelectItem key={s} value={s}>{s.replace("_", " ")}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {active.length > 0 && (
        <div className="flex flex-wrap items-center gap-2" data-testid="active-filters">
          {active.map((k) => (
            <button key={k} onClick={() => drop(k)} data-testid={`filter-chip-${k}`}
              className="inline-flex items-center gap-1.5 rounded-full bg-indigo-50 px-3 py-1.5 text-xs font-bold text-indigo-700 hover:bg-indigo-100">
              {FILTER_LABELS[k]}: {chipText(k, params.get(k), rows)} <X className="h-3.5 w-3.5" />
            </button>
          ))}
          <button onClick={() => setParams({})} className="text-xs font-semibold text-slate-500 underline hover:text-slate-800" data-testid="clear-all-filters">Clear all</button>
          <span className="text-xs text-slate-400" data-testid="feedback-count">{rows.length} rating{rows.length === 1 ? "" : "s"}</span>
        </div>
      )}

      {rows.length === 0 ? <EmptyState icon={MessageSquare} title="No feedback" hint={active.length ? "No ratings match these filters." : "Customer feedback will appear here."} /> : (
        <div className="space-y-3">
          {rows.map((f) => (
            <FeedbackCard key={f.id} f={f} onOpen={(id) => setParam("open", id)} actions={<>
              <Button size="sm" variant="outline" onClick={() => moderate(f, "TRUSTED")} data-testid={`trust-${f.id}`}>Mark trusted</Button>
              <Button size="sm" variant="outline" onClick={() => moderate(f, "REJECTED")} data-testid={`reject-${f.id}`} className="text-red-600">Reject</Button>
            </>} />
          ))}
        </div>
      )}
      <FeedbackDetailSheet feedbackId={openId} onClose={() => setParam("open", "")} onChanged={load} />
    </div>
  );
}
